import { useEffect, useState } from "react";
import { useIntent } from "@/context/IntentContext";

const STORAGE_KEY = "smt_exit_intent_shown";

/**
 * Exit-intent popup (desktop only). Triggered when cursor moves toward browser chrome.
 * Shown once per session (localStorage flag).
 */
export function ExitIntentPopup() {
  const { open: openIntent, isOpen: intentOpen } = useIntent();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.innerWidth < 768) return; // desktop only
    if (sessionStorage.getItem(STORAGE_KEY)) return; // shown this session

    const handler = (e: MouseEvent) => {
      if (e.clientY <= 0 && !intentOpen) {
        setShow(true);
        sessionStorage.setItem(STORAGE_KEY, "1");
      }
    };

    document.addEventListener("mouseleave", handler);
    return () => document.removeEventListener("mouseleave", handler);
  }, [intentOpen]);

  if (!show || intentOpen) return null;

  const handleShowMe = () => {
    setShow(false);
    openIntent();
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-6"
      style={{ background: "rgba(8,8,18,0.7)", backdropFilter: "blur(6px)", animation: "fade-in 250ms ease-out" }}
      onClick={() => setShow(false)}
    >
      <div
        className="relative max-w-md w-full p-7 sm:p-9 text-center"
        style={{
          background: "linear-gradient(180deg, #313949 0%, #1e1f2c 100%)",
          border: "1px solid rgba(234,172,85,0.25)",
          boxShadow: "0 0 40px rgba(234,172,85,0.15), 0 24px 60px rgba(0,0,0,0.6)",
          animation: "scale-up 350ms cubic-bezier(0.22, 1, 0.36, 1)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => setShow(false)}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <p className="text-[10px] uppercase tracking-[0.25em] font-medium mb-3" style={{ color: "#eaac55" }}>
          Hold up
        </p>

        <h3
          className="font-extrabold text-text-primary leading-[1.05] tracking-tight mb-3"
          style={{ fontSize: "clamp(22px, 3vw, 28px)" }}
        >
          Before you go &mdash; your show data isn't going anywhere.
          <br />
          <span style={{ color: "#eaac55" }}>But your memory of what happened is.</span>
        </h3>

        <p className="text-text-secondary text-sm leading-relaxed mb-6">
          Tell us where you are in your Fringe journey and we'll show you what your data says. Takes 90 seconds.
        </p>

        <button
          onClick={handleShowMe}
          className="w-full py-3 font-bold uppercase tracking-[0.12em] text-[12px] text-white transition-all hover:brightness-110"
          style={{
            background: "linear-gradient(135deg, #eaac55, #d4953e)",
            boxShadow: "0 0 24px rgba(234,172,85,0.4)",
          }}
        >
          Show Me {"\u2192"}
        </button>

        <button
          onClick={() => setShow(false)}
          className="mt-3 text-[11px] text-text-muted hover:text-text-secondary transition-colors"
        >
          No thanks, I'll keep guessing
        </button>
      </div>
    </div>
  );
}
